import Swal from "sweetalert2";
import { obtenerPdfAlquiler, obtenerPdfVentas, obtenerPdfVentasRapidas } from "../api/Pdf";
import { NOMBRE_NEGOCIO } from "../constants/negocio";
import compartirPdfWhatsApp, { abrirChatWhatsApp } from "./compartirPdfWhatsApp";

// Envía por WhatsApp el PDF de una factura, cotización o alquiler.
//
// El navegador solo permite abrir el menú "Compartir" o una ventana nueva
// inmediatamente después de un clic. Descargar el PDF tarda, así que se hace
// en dos pasos: primero se prepara el PDF y luego el usuario confirma con
// "Enviar por WhatsApp", que comparte en el acto con el archivo ya listo.

// Color de WhatsApp tomado de la paleta (var.scss → --color-whatsapp)
const colorWhatsApp = () =>
    getComputedStyle(document.documentElement).getPropertyValue("--color-whatsapp").trim() || undefined;

const compartirDocumentoWhatsApp = async ({ obtenerPdf, documento, numero, mensaje, celular }) => {
    Swal.fire({
        title: `Preparando ${documento.toLowerCase()}...`,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
    });

    let blob;
    try {
        blob = await obtenerPdf();
    } catch {
        Swal.fire({ title: "Error", text: "No se pudo generar el PDF. Verifique la conexión.", icon: "error" });
        return;
    }

    const { isConfirmed } = await Swal.fire({
        title: `PDF listo: ${documento} No. ${numero}`,
        text: celular
            ? `Se enviará al WhatsApp ${celular}`
            : "El cliente no tiene celular registrado: elige el contacto en WhatsApp",
        icon: "success",
        confirmButtonText: "Enviar por WhatsApp",
        confirmButtonColor: colorWhatsApp(),
        showCancelButton: true,
        cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    const nombreArchivo = `${documento.toLowerCase().replace(/\s+/g, "-")}-${numero}.pdf`;
    const resultado = await compartirPdfWhatsApp({ blob, nombreArchivo, mensaje, celular });

    if (resultado === "descargado") {
        Swal.fire({
            title: "PDF descargado",
            text: "Se abrió WhatsApp: adjunta el PDF descargado en el chat.",
            icon: "info",
        });
    } else if (resultado === "compartido") {
        // En el celular WhatsApp envía solo el PDF y descarta el texto, así que
        // el mensaje se manda aparte (requiere un nuevo clic del usuario)
        const { isConfirmed: enviarMensaje } = await Swal.fire({
            title: "PDF enviado",
            text: celular
                ? `¿Enviar también el mensaje al WhatsApp ${celular}?`
                : "¿Enviar también el mensaje? Elige el mismo contacto en WhatsApp.",
            icon: "success",
            confirmButtonText: "Enviar mensaje",
            confirmButtonColor: colorWhatsApp(),
            showCancelButton: true,
            cancelButtonText: "No, gracias",
        });
        if (enviarMensaje) abrirChatWhatsApp({ mensaje, celular });
    }
};

// Número visible del documento: el consecutivo si existe, si no el inicio del id
const numeroDocumento = ({ numeroFactura, id }) =>
    numeroFactura || String(id || "").slice(0, 8).toUpperCase();

const nombreCliente = (registro) => {
    const persona = registro?.Cliente?.Persona;
    return (registro?.nombre || [persona?.nombre, persona?.apellido].filter(Boolean).join(" ")).trim();
};

// *texto* = negrilla y _texto_ = cursiva en WhatsApp. Se arma por líneas para
// que la sangría del código no se cuele como espacios en el mensaje.
const armarMensaje = ({ documento, numero, cliente, cuerpo }) =>
    [
        `🪵 *${NOMBRE_NEGOCIO}*`,
        `🧾 ${documento} No. *${numero}*`,
        "",
        `¡Hola${cliente ? ` ${cliente}` : ""}! 👋 ${cuerpo}`,
        "📎 En el PDF adjunto encontrarás todo el detalle.",
        "",
        "🙏 ¡Gracias por preferirnos!",
    ].join("\n");

// Factura o cotización (módulo Factura / Cotización)
export const compartirFacturaWhatsApp = (factura) => {
    const esCotizacion = factura?.tipo === "cotizacion";
    const documento = esCotizacion ? "Cotización" : "Factura";
    const numero = numeroDocumento(factura);

    return compartirDocumentoWhatsApp({
        obtenerPdf: () => obtenerPdfVentas(factura.id),
        documento,
        numero,
        celular: factura?.Cliente?.Persona?.celular,
        mensaje: armarMensaje({
            documento,
            numero,
            cliente: nombreCliente(factura),
            cuerpo: esCotizacion ? "Te compartimos la cotización solicitada." : "Te compartimos la factura de tu compra.",
        }),
    });
};

// Venta rápida o cotización rápida
export const compartirVentaRapidaWhatsApp = (venta) => {
    const esCotizacion = venta?.tipo === "cotizacion";
    const documento = esCotizacion ? "Cotización" : "Factura";
    const numero = numeroDocumento(venta);

    return compartirDocumentoWhatsApp({
        obtenerPdf: () => obtenerPdfVentasRapidas(venta.id),
        documento,
        numero,
        celular: venta?.telefono || venta?.Cliente?.Persona?.celular,
        mensaje: armarMensaje({
            documento,
            numero,
            cliente: nombreCliente(venta),
            cuerpo: esCotizacion ? "Te compartimos la cotización solicitada." : "Te compartimos la factura de tu compra.",
        }),
    });
};

// Comprobante de alquiler
export const compartirAlquilerWhatsApp = (alquiler) => {
    const documento = "Alquiler";
    const numero = numeroDocumento(alquiler);

    return compartirDocumentoWhatsApp({
        obtenerPdf: () => obtenerPdfAlquiler(alquiler.id),
        documento,
        numero,
        celular: alquiler?.telefono || alquiler?.Cliente?.Persona?.celular,
        mensaje: armarMensaje({
            documento,
            numero,
            cliente: nombreCliente(alquiler),
            cuerpo: "Te compartimos el comprobante de tu alquiler.",
        }),
    });
};
